import {
  AfterContentInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, BehaviorSubject, of } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  DataCellEvent,
  DataColumn,
  DataSorting,
  DataTableComponent,
  ShowHeaderMode,
  ObjectDataColumn,
  DataColumnListComponent,
  PaginationModel,
  ThumbnailService,
  CustomLoadingContentTemplateDirective,
  CustomNoPermissionTemplateDirective,
  CustomEmptyContentTemplateDirective,
  RequestPaginationModel,
  DataRow,
  UserPreferenceValues,
  PaginatedComponent,
} from '@alfresco/adf-core';
import { Node, NodeEntry, NodePaging, Pagination } from '@alfresco/js-api';
import {
  ContentActionModel,
  ContentService,
  DocumentListComponent,
  FilterSearch,
  NodeEntityEvent,
  NodeEntryEvent,
  PermissionStyleModel,
  RowFilter,
  ShareDataRow,
  ShareDataTableAdapter,
} from '@alfresco/adf-content-services';
import { DocumentLoaderNode } from '@alfresco/adf-content-services/lib/document-list/models/document-folder.model';

import { WkAppConfigService } from '../../../services/wk-app-config.service';
import { WkAlfrescoApiService } from '../../../services/wk-alfresco-api.service';
import { WkDocumentListService } from '../../../services/wk-document-list.service';
import { presetsDefaultModel } from '../../models/preset.model';
import { WkAlfrescoApplicationService } from '../../../services/wk-alfresco-application.service';
import { AppStore } from '../../../store/states/app.state';
import { SetSelectedDocumentsAction } from '../../../store';
import { CommonConstants } from '../../../shared';

@Component({
  selector: 'wk-document-list',
  templateUrl: './wk-document-list.component.html',
  styleUrls: ['./wk-document-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'wk-document-list' },
  standalone: false,
})
export class WkDocumentListComponent
  implements OnInit, OnChanges, OnDestroy, AfterContentInit, PaginatedComponent
{
  static SINGLE_CLICK_NAVIGATION = 'click';
  static DOUBLE_CLICK_NAVIGATION = 'dblclick';

  DEFAULT_PAGINATION: Pagination = new Pagination({
    hasMoreItems: false,
    skipCount: 0,
    maxItems: 25,
    totalItems: 0,
  });

  DEFAULT_SORTING: DataSorting[] = [
    new DataSorting('name', 'asc'),
    new DataSorting('isFolder', 'desc'),
  ];

  @ContentChild(DataColumnListComponent)
  columnList: DataColumnListComponent;

  @ContentChild(CustomLoadingContentTemplateDirective)
  customLoadingContent: CustomLoadingContentTemplateDirective;

  @ContentChild(CustomNoPermissionTemplateDirective)
  customNoPermissionsTemplate: CustomNoPermissionTemplateDirective;

  @ContentChild(CustomEmptyContentTemplateDirective)
  customNoContentTemplate: CustomEmptyContentTemplateDirective;
  @Input()
  includeFields: string[];
  @Input()
  where: string;
  @Input()
  permissionsStyle: PermissionStyleModel[] = [];
  @Input()
  locationFormat = '/';
  @Input()
  navigate = true;
  @Input()
  showHeader = ShowHeaderMode.Data;
  @Input()
  navigationMode = DocumentListComponent.DOUBLE_CLICK_NAVIGATION;
  @Input()
  thumbnails = false;
  @Input()
  selectionMode = 'single';
  @Input()
  multiselect = false;
  @Input()
  contentActions = false;
  @Input()
  contentActionsPosition = 'right';
  @Input()
  contextMenuActions = false;
  @Input()
  emptyFolderImageUrl = './assets/images/empty_doc_lib.svg';
  @Input()
  allowDropFiles = false;
  @Input()
  sorting: string[] | DataSorting = ['name', 'asc'];
  @Input()
  additionalSorting: DataSorting = new DataSorting('isFolder', 'desc');
  @Input()
  sortingMode = 'server';
  @Input()
  rowStyle: string;
  @Input()
  rowStyleClass: string;
  @Input()
  loading = false;
  @Input()
  _rowFilter: RowFilter | null = null;
  @Input()
  set rowFilter(rowFilter: RowFilter) {
    this._rowFilter = rowFilter;
    if (this.data) {
      this.data.setFilter(this._rowFilter);
      if (this.currentFolderId) {
        this.reload();
      }
    }
  }

  get rowFilter(): RowFilter {
    return this._rowFilter;
  }
  @Input()
  imageResolver: any | null = null;
  @Input()
  stickyHeader = false;
  @Input()
  headerFilters = false;
  @Input()
  filterValue: any;
  @Input()
  currentFolderId: string = null;
  @Input()
  preselectNodes: NodeEntry[] = [];
  @Input()
  node: NodePaging = null;
  @Input()
  maxItems: number = this.DEFAULT_PAGINATION.maxItems;
  @Output()
  nodeClick: EventEmitter<NodeEntityEvent> =
    new EventEmitter<NodeEntityEvent>();
  @Output()
  nodeDblClick: EventEmitter<NodeEntityEvent> =
    new EventEmitter<NodeEntityEvent>();
  @Output()
  folderChange: EventEmitter<NodeEntryEvent> =
    new EventEmitter<NodeEntryEvent>();
  @Output()
  preview: EventEmitter<NodeEntityEvent> = new EventEmitter<NodeEntityEvent>();
  @Output()
  ready: EventEmitter<NodePaging> = new EventEmitter();
  @Output()
  loadError: EventEmitter<any> = new EventEmitter();
  @Output()
  nodeSelected: EventEmitter<NodeEntry[]> = new EventEmitter<NodeEntry[]>();
  @Output()
  filterSelection: EventEmitter<FilterSearch[]> = new EventEmitter();

  @ViewChild('dataTable', { static: true })
  dataTable: DataTableComponent;

  actions: ContentActionModel[] = [];
  contextActionHandler: Subject<any> = new Subject<void>();
  data: ShareDataTableAdapter;
  noPermission = false;
  selection = new Array<NodeEntry>();
  $folderNode: Subject<Node> = new Subject<Node>();
  allowFiltering = true;
  orderBy: string[] = null;

  folderNode: Node;

  private _wkPagination: PaginationModel;
  pagination: BehaviorSubject<PaginationModel>;
  sortingSubject: BehaviorSubject<DataSorting[]> = new BehaviorSubject<
    DataSorting[]
  >(this.DEFAULT_SORTING);

  private wkLayoutPresets = {};
  private wkRowMenuCache: { [key: string]: ContentActionModel[] } = {};
  private wkLoadingTimeout: any;
  private readonly _onDestroy$ = new Subject<boolean>();
  private readonly onUrlChange$ = new Subject<void>();
  protected readonly wkDocumentListService: WkDocumentListService;
  protected readonly wkNgZone: NgZone;
  protected readonly wkAppConfig: WkAppConfigService;
  protected readonly wkContentService: ContentService;
  protected readonly wkApplicationService: WkAlfrescoApplicationService;
  protected readonly apiService: WkAlfrescoApiService;
  protected readonly wkElementRef: ElementRef;
  protected readonly wkRouter: Router;
  private readonly originThumbnailService: ThumbnailService;

  constructor(injector: Injector, private readonly store: Store<AppStore>) {
    this.wkDocumentListService = injector.get<WkDocumentListService>(
      WkDocumentListService
    );
    this.wkNgZone = injector.get<NgZone>(NgZone);
    this.wkAppConfig = injector.get<WkAppConfigService>(WkAppConfigService);
    this.wkApplicationService = injector.get<WkAlfrescoApplicationService>(
      WkAlfrescoApplicationService
    );
    this.wkContentService = injector.get<ContentService>(ContentService);
    this.apiService = injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this._wkPagination =
      this.wkDocumentListService.pagination || this.DEFAULT_PAGINATION;
    this.pagination = new BehaviorSubject<PaginationModel>(this._wkPagination);
    this.wkElementRef = injector.get<ElementRef>(ElementRef);
    this.wkRouter = injector.get<Router>(Router);
    this.originThumbnailService =
      injector.get<ThumbnailService>(ThumbnailService);
    this.wkApplicationService.userPreferences
      .select(UserPreferenceValues.PaginationSize)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((pagSize) => {
        this.maxItems = this._wkPagination.maxItems = pagSize;
      });
  }

  getContextActions(nodeVal: NodeEntry | any) {
    if (nodeVal && nodeVal.entry) {
      const actions = this.getNodeActions(nodeVal);
      if (actions && actions.length > 0) {
        return actions.map((currentAction: ContentActionModel) => ({
          model: currentAction,
          node: nodeVal,
          subject: this.contextActionHandler,
        }));
      }
    }
    return null;
  }

  get hasCustomLayout(): boolean {
    return (
      this.columnList &&
      this.columnList.columns &&
      this.columnList.columns.length > 0
    );
  }

  private fetchDefaultSorting(): DataSorting {
    let defaultSorting: DataSorting;
    if (Array.isArray(this.sorting)) {
      const [key, direction] = this.sorting;
      defaultSorting = new DataSorting(key, direction);
    } else {
      defaultSorting = new DataSorting(
        this.sorting.key,
        this.sorting.direction
      );
    }

    return defaultSorting;
  }

  private fetchLayoutPreset(name = 'default'): DataColumn[] {
    return (this.wkLayoutPresets[name] || this.wkLayoutPresets['default']).map(
      (col) => new ObjectDataColumn(col)
    );
  }

  isMobile(): boolean {
    return !!/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  isEmpty() {
    return !this.data || this.data.getRows().length === 0;
  }

  ngOnInit() {
    this.wkRowMenuCache = {};
    this.loadPresetsOfLayout();
    this.data = new ShareDataTableAdapter(
      this.originThumbnailService,
      this.wkContentService,
      null,
      this.fetchDefaultSorting(),
      this.sortingMode,
      this.allowDropFiles
    );
    this.data.thumbnails = this.thumbnails;
    this.data.permissionsStyle = this.permissionsStyle;

    this.wkRouter.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onUrlChange$)
      )
      .subscribe((event: NavigationEnd) => {
        if (
          event.urlAfterRedirects.includes('preview') ||
          event.urlAfterRedirects.includes('task-detail')
        ) {
          this.wkDocumentListService.preservePaginationState(
            this._wkPagination
          );
          this.onUrlChange$.next();
          this.onUrlChange$.complete();
        } else if (event.urlAfterRedirects.includes('libraries')) {
          this._wkPagination = this.DEFAULT_PAGINATION;
        } else if (!event.urlAfterRedirects.includes('libraries')) {
          this.wkDocumentListService.destroyPaginationState();
          this.onUrlChange$.next();
          this.onUrlChange$.complete();
        }
      });

    if (this._rowFilter) {
      this.data.setFilter(this._rowFilter);
    }

    if (this.imageResolver) {
      this.data.setImageResolver(this.imageResolver);
    }

    this.contextActionHandler
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((val) => this.contextActionCallback(val));

    this.enforceSingleClickNavigationForMobiledevices();
  }

  ngAfterContentInit() {
    if (this.columnList) {
      this.columnList.columns.changes
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(() => this.setchemaforTable());
    }
    this.setchemaforTable();
  }

  private setchemaforTable() {
    let schema: DataColumn[] = [];

    if (this.hasCustomLayout) {
      schema = this.columnList.columns.map((c) => c as DataColumn);
    }

    if (!this.data) {
      this.data = new ShareDataTableAdapter(
        this.originThumbnailService,
        this.wkContentService,
        schema,
        this.fetchDefaultSorting(),
        this.sortingMode
      );
    } else if (schema && schema.length > 0) {
      this.data.setColumns(schema);
    }

    const columns = this.data.getColumns();
    if (!columns || columns.length === 0) {
      this.setupDefaultColumns(this.currentFolderId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.resetSelection();
    this.dataSorting();
    this.dataThumbnail();
    this.sortingModeChanges(changes);
    this.dataSortingMode(changes);

    if (
      this.currentFolderId &&
      changes['currentFolderId']?.currentValue !==
        changes['currentFolderId']?.previousValue
    ) {
      this.loadFolder();
    }

    if (this.data) {
      if (changes.node && changes.node.currentValue) {
        const merge = this._wkPagination ? this._wkPagination.merge : false;
        this.data.loadPage(changes.node.currentValue, merge, null);
        this.whenPreselectNodes();
        this.whenDataReady(changes.node.currentValue);
      } else if (changes.imageResolver) {
        this.data.setImageResolver(changes.imageResolver.currentValue);
      }
    }
  }

  dataThumbnail() {
    if (this.data) {
      this.data.thumbnails = this.thumbnails;
    }
  }

  dataSorting() {
    if (Array.isArray(this.sorting)) {
      const [key, direction] = this.sorting;
      this.orderBy = this.buildOrderfromArray(key, direction);
    } else {
      this.orderBy = this.buildOrderfromArray(
        this.sorting.key,
        this.sorting.direction
      );
    }
  }

  dataSortingMode(changes: SimpleChanges) {
    if (changes.sorting && !changes.sorting.firstChange && this.data) {
      const newValue = changes.sorting.currentValue;
      if (newValue && newValue.length > 0) {
        const [key, direction] = newValue;
        this.data.setSorting(new DataSorting(key, direction));
      }
    }
  }

  sortingModeChanges(changes: SimpleChanges) {
    if (changes.sortingMode && !changes.sortingMode.firstChange && this.data) {
      this.data.sortingMode = changes.sortingMode.currentValue;
    }
  }

  reload() {
    this.wkNgZone.run(() => {
      this.resetSelection();
      if (this.node) {
        this.data.loadPage(this.node, this._wkPagination.merge, null);
        this.whenPreselectNodes();
        this.synchronizePagination();
        this.whenDataReady(this.node);
      } else {
        this.loadFolder();
      }
    });
  }

  contextActionCallback(action) {
    if (action) {
      this.executeContentAction(action.node, action.model);
    }
  }

  getNodeActions(node: NodeEntry | any): ContentActionModel[] {
    if (node && node.entry) {
      let target = null;

      if (node.entry.isFile) {
        target = 'document';
      } else if (node.entry.isFolder) {
        target = 'folder';
      }

      if (target) {
        const actions = this.wkRowMenuCache[node.entry.id];
        if (actions) {
          actions.forEach((action) => {
            this.updateAction(action, node);
          });
          return actions;
        }

        const actionsByTarget = this.actions
          .filter((entry) => {
            const isVisible =
              typeof entry.visible === 'function'
                ? entry.visible(node)
                : entry.visible;

            return isVisible && entry.target.toLowerCase() === target;
          })
          .map((action) => new ContentActionModel(action));

        actionsByTarget.forEach((action) => {
          this.updateAction(action, node);
        });

        this.wkRowMenuCache[node.entry.id] = actionsByTarget;
        return actionsByTarget;
      }
    }

    return [];
  }

  private updateAction(action: ContentActionModel, node: NodeEntry) {
    action.disabled = this.cannotPerformAction(action, node);
    action.visible = this.canSeeAction(action, node);
  }

  private canSeeAction(action: ContentActionModel, node: NodeEntry): boolean {
    if (typeof action.visible === 'function') {
      return action.visible(node);
    }

    return action.visible;
  }

  private cannotPerformAction(
    action: ContentActionModel,
    node: NodeEntry
  ): boolean {
    if (typeof action.disabled === 'function') {
      return action.disabled(node);
    }

    if (
      (action.permission &&
        action.disableWithNoPermission &&
        !this.wkContentService.hasAllowableOperations(
          node.entry,
          action.permission
        )) ||
      this.wkApplicationService.lockInstance.isLocked(node.entry)
    ) {
      return true;
    } else {
      return action.disabled;
    }
  }

  @HostListener('contextmenu', ['$event'])
  onShowContextMenu(e?: Event) {
    if (e && this.contextMenuActions) {
      e.preventDefault();
    }
  }

  navigateTo(node: Node | string): boolean {
    if (typeof node === 'string') {
      this.resetPaginationofNewFolder();
      this.currentFolderId = node;
      this.folderChange.emit(new NodeEntryEvent({ id: node } as Node));
      this.reload();
      return true;
    } else {
      if (this.canNavigateFolder(node)) {
        this.resetPaginationofNewFolder();
        this.currentFolderId = this.getFolderNodeDestinationId(node);
        this.folderChange.emit(
          new NodeEntryEvent({ id: this.currentFolderId } as Node)
        );
        this.reload();
        return true;
      }
    }
    return false;
  }

  private getFolderNodeDestinationId(node: Node) {
    return this.isfolderLink(node)
      ? node.properties['cm:destination']
      : node.id;
  }

  private isfolderLink(node: Node) {
    return (
      node.nodeType === 'app:folderlink' &&
      node.properties &&
      node.properties['cm:destination']
    );
  }

  updateCustomSourceData(nodeId: string): void {
    this.currentFolderId = nodeId;
  }

  executeContentAction(node: NodeEntry, action: ContentActionModel) {
    if (node && node.entry && action) {
      let handlerSub;

      if (typeof action.handler === 'function') {
        handlerSub = action.handler(node, this, action.permission);
      } else {
        handlerSub = of(true);
      }

      if (typeof action.execute === 'function' && handlerSub) {
        handlerSub
          .pipe(takeUntil(this._onDestroy$))
          .subscribe(() => action.execute(node));
      }
    }
  }

  private setOnLoadState(value: boolean) {
    if (this.data?.getRows().length > 0) {
      if (value) {
        clearTimeout(this.wkLoadingTimeout);
        this.wkLoadingTimeout = setTimeout(() => {
          this.loading = true;
        }, 1000);
      } else {
        clearTimeout(this.wkLoadingTimeout);
        this.loading = false;
      }
    } else {
      clearTimeout(this.wkLoadingTimeout);
      this.loading = value;
    }
  }

  loadFolder() {
    if (!this._wkPagination.merge) {
      this.setOnLoadState(true);
    }

    if (!this.hasCustomLayout) {
      this.setupDefaultColumns(this.currentFolderId);
    }

    if (
      this.wkDocumentListService.isCustomSourceService(this.currentFolderId)
    ) {
      this.updateCustomSourceData(this.currentFolderId);
    }

    this.wkDocumentListService
      .loadFolderByNodeId(
        this.currentFolderId,
        this._wkPagination,
        this.includeFields,
        this.where,
        this.orderBy
      )
      .subscribe({
        next: (documentNode: DocumentLoaderNode) => {
          if (documentNode.currentNode) {
            this.folderNode = documentNode.currentNode.entry;
            this.$folderNode.next(documentNode.currentNode.entry);
          }
          this.onPageLoaded(documentNode.children);
        },
        error: (err) => {
          this._handleError(err);
        },
      });
  }

  resetSelection() {
    this.dataTable.resetSelection();
    this.selection = [];
    this.noPermission = false;
    this.store.dispatch(new SetSelectedDocumentsAction([]));
  }

  onPageLoaded(nodePaging: NodePaging) {
    if (nodePaging) {
      this.data.loadPage(
        nodePaging,
        this._wkPagination.merge,
        this.allowDropFiles
      );
      this.whenPreselectNodes();
      this.setOnLoadState(false);
      this.whenDataReady(nodePaging);
    }
  }

  onSortingChanged(event: Event) {
    const localCastEvent = event as CustomEvent;
    this.orderBy = this.buildOrderfromArray(
      localCastEvent.detail.sortingKey,
      localCastEvent.detail.direction
    );
    this.reload();
    this.sortingSubject.next([this.additionalSorting, localCastEvent.detail]);
  }

  private buildOrderfromArray(
    currentKey: string,
    currentDirection: string
  ): string[] {
    return [
      `${this.additionalSorting.key} ${this.additionalSorting.direction}`,
      `${currentKey} ${currentDirection}`,
    ];
  }

  setupDefaultColumns(preset = 'default'): void {
    if (this.data) {
      const columns = this.fetchLayoutPreset(preset);
      this.data.setColumns(columns);
    }
  }

  onPreviewFile(node: NodeEntry) {
    if (node) {
      this.preview.emit(new NodeEntityEvent(node));
    }
  }

  onNodeClick(nodeEntry: NodeEntry) {
    const domEvent = new CustomEvent('node-click', {
      detail: {
        sender: this,
        node: nodeEntry,
      },
      bubbles: true,
    });

    this.wkElementRef.nativeElement.dispatchEvent(domEvent);

    const event = new NodeEntityEvent(nodeEntry);
    this.nodeClick.emit(event);

    if (
      !event.defaultPrevented &&
      this.navigate &&
      this.navigationMode === DocumentListComponent.SINGLE_CLICK_NAVIGATION
    ) {
      this.executeActionClick(nodeEntry);
    }
  }

  onNodeDblClick(nodeEntry: NodeEntry) {
    const domEvent = new CustomEvent('node-dblclick', {
      detail: {
        sender: this,
        node: nodeEntry,
      },
      bubbles: true,
    });
    this.wkElementRef.nativeElement.dispatchEvent(domEvent);

    const event = new NodeEntityEvent(nodeEntry);
    this.nodeDblClick.emit(event);

    if (
      !event.defaultPrevented &&
      this.navigate &&
      this.navigationMode === DocumentListComponent.DOUBLE_CLICK_NAVIGATION
    ) {
      this.executeActionClick(nodeEntry);
    }
  }

  executeActionClick(nodeEntry: NodeEntry) {
    if (nodeEntry && nodeEntry.entry) {
      if (nodeEntry.entry.isFile) {
        this.onPreviewFile(nodeEntry);
      }

      if (nodeEntry.entry.isFolder) {
        this.navigateTo(nodeEntry.entry);
      }

      if (nodeEntry.entry['guid']) {
        const options = {
          include: this.includeFields,
        };

        this.apiService.nodesApi
          .getNode(nodeEntry.entry['guid'], options)
          .then((node: NodeEntry) => {
            this.navigateTo(node.entry);
          });
      }
    }
  }

  onNodeSelect(event: { row: ShareDataRow; selection: Array<ShareDataRow> }) {
    const selectionData = event.selection
      .filter(({ node }) => !node?.entry?.isFolder)
      .map(({ id, node }) => ({
        id,
        name: node?.entry?.name,
        url: node?.entry?.properties?.[CommonConstants.APOLLO_TENANT_ID],
      }));
    this.store.dispatch(new SetSelectedDocumentsAction(selectionData));
    this.selection = event.selection.map((entry) => entry.node);
    const domEvent = new CustomEvent('node-select', {
      detail: {
        node: event.row ? event.row.node : null,
        selection: this.selection,
      },
      bubbles: true,
    });
    this.nodeSelected.emit(this.selection);
    this.wkElementRef.nativeElement.dispatchEvent(domEvent);
  }

  onNodeUnselect(event: { row: ShareDataRow; selection: Array<ShareDataRow> }) {
    const selectionData = event.selection
      .filter(({ node }) => !node?.entry?.isFolder)
      .map(({ id, node }) => ({
        id,
        name: node?.entry?.name,
      }));
    this.store.dispatch(new SetSelectedDocumentsAction(selectionData));
    this.selection = event.selection.map((entry) => entry.node);
    const domEvent = new CustomEvent('node-unselect', {
      detail: {
        node: event.row ? event.row.node : null,
        selection: this.selection,
      },
      bubbles: true,
    });
    this.nodeSelected.emit(this.selection);
    this.wkElementRef.nativeElement.dispatchEvent(domEvent);
  }

  onShowRowContextMenu(event: DataCellEvent) {
    if (this.contextMenuActions) {
      const args = event.value;
      const node = args.row.node as ShareDataRow;
      if (node) {
        args.actions = this.getContextActions(node) || [];
      }
    }
  }

  onShowRowActionsMenu(event: DataCellEvent) {
    if (this.contentActions) {
      const args = event.value;
      const node = args.row.node as ShareDataRow;
      if (node) {
        args.actions = this.getNodeActions(node) || [];
      }
    }
  }

  private enforceSingleClickNavigationForMobiledevices(): void {
    if (this.isMobile()) {
      this.navigationMode = DocumentListComponent.SINGLE_CLICK_NAVIGATION;
    }
  }

  canNavigateFolder(node: Node): boolean {
    let canNavigateFolder = false;

    if (node && node.isFolder) {
      canNavigateFolder = true;
    }

    return canNavigateFolder;
  }

  private loadPresetsOfLayout(): void {
    const externalSettings = this.wkAppConfig.get(
      'document-list.presets',
      null
    );

    if (externalSettings) {
      this.wkLayoutPresets = Object.assign(
        {},
        presetsDefaultModel,
        externalSettings
      );
    } else {
      this.wkLayoutPresets = presetsDefaultModel;
    }
  }

  private whenDataReady(nodePaging: NodePaging) {
    this.ready.emit(nodePaging);
    this.pagination.next(nodePaging.list.pagination);
  }

  updatePagination(requestPaginationModel: RequestPaginationModel) {
    this._wkPagination.maxItems = requestPaginationModel.maxItems;
    this._wkPagination.merge = requestPaginationModel.merge;
    this._wkPagination.skipCount = requestPaginationModel.skipCount;
    this.reload();
  }

  private synchronizePagination() {
    this.node.list.pagination.maxItems = this._wkPagination.maxItems;
    this.node.list.pagination.skipCount = this._wkPagination.skipCount;
  }

  onFilterSelectionChange(activeFilters: FilterSearch[]) {
    this.filterSelection.emit(activeFilters);
  }

  private resetPaginationofNewFolder() {
    this._wkPagination.skipCount = 0;
    this._wkPagination.maxItems = this.maxItems;
  }

  ngOnDestroy() {
    this._onDestroy$.next(true);
    this._onDestroy$.complete();
  }

  private _handleError(err: any) {
    if (err.message) {
      try {
        if (JSON.parse(err.message).error.statusCode === 403) {
          this.noPermission = true;
        }
      } catch (error) {}
    }
    this.setOnLoadState(false);
    this.loadError.emit(err);
  }

  private getPreSelectedRowsBasedOnSelectionMode(): DataRow[] {
    if (this.hasPreselectNodes()) {
      return [];
    }

    const preSelectedNodes = this.getPreSelectedRows();

    return this.isSingleSelectionMode()
      ? [preSelectedNodes[0]]
      : preSelectedNodes;
  }

  private getPreSelectedRows(): DataRow[] {
    const preSelectedNodeIds = this.preselectNodes.map(
      ({ entry: { id } }) => id
    );
    return this.data
      .getRows()
      .filter(({ id }) => preSelectedNodeIds.includes(id));
  }

  private whenPreselectNodes() {
    if (this.hasPreselectNodes()) {
      this.onNodeSelect({
        row: undefined,
        selection:
          this.getPreSelectedRowsBasedOnSelectionMode() as ShareDataRow[],
      });
    }
  }

  isSingleSelectionMode(): boolean {
    return this.selectionMode === 'single';
  }

  hasPreselectNodes(): boolean {
    return this.preselectNodes && this.preselectNodes.length > 0;
  }
}
